import findIndex from 'lodash/findIndex.js';
import sortBy from 'lodash/sortBy.js';

import Model from '@/models/Model.js';
import TimePeriod from '@/models/TimePeriod.js';

export default class Section extends Model {
    static entity = 'sections';

    static modelName = 'Section';

    static fields() {
        return {
            id: this.attr(null),
            uuid: this.attr(null),
            section_settings_id: this.attr(null),
            course_id: this.attr(null),
            parent_id: this.attr(null),
            section_template_id: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            is_active: this.attr(null),
            order: this.attr(null),

            ...super.fields(),

            course: this.belongsTo('courses', 'course_id'),
            parent: this.belongsTo('sections', 'parent_id'),
            children: this.hasMany('sections', 'parent_id'),
            section_template: this.belongsTo('section_templates', 'section_template_id'),
            section_settings: this.belongsTo('section_settings', 'section_settings_id'),
            activities: this.hasMany('activities', 'activities_id'),
            prerequisite: this.belongsTo('prerequisites', 'prerequisite_id'),
            time_periods: this.hasMany('time_periods', 'section_id'),
            upcoming_time_periods: this.hasMany('time_periods', 'section_id'),
            objective: this.morphOne('objectives', 'subject_id', 'subject_type_entity'),
        };
    }

    get scheduled_time() {
        const totals = {
            total_minutes: 0,
            total_break_minutes: 0,
            hours: 0,
            minutes: 0,
            break_hours: 0,
            break_minutes: 0,
        };

        this.time_periods.forEach(timePeriod => {
            totals.total_minutes += timePeriod.scheduled_time.total_minutes;
            totals.total_break_minutes += timePeriod.scheduled_time.break_minutes;
        });

        totals.hours = Math.floor(totals.total_minutes / 60);
        totals.minutes = totals.total_minutes - totals.hours * 60;

        totals.break_hours = Math.floor(totals.total_break_minutes / 60);
        totals.break_minutes = totals.total_break_minutes - totals.break_hours * 60;

        return totals;
    }

    get is_week() {
        return this.section_settings.type === 'week';
    }

    get is_break() {
        return this.section_settings.type === 'break';
    }

    get is_topic() {
        return this.section_settings.type === 'topic';
    }

    get display_title() {
        return this.is_week ? `Week ${this.display_week_number}` : this.title;
    }

    get display_week_number() {
        const allSections = sortBy(
            Section.query()
                .with('section_settings')
                .where('course_id', this.course_id)
                .whereHas('section_settings', query => query.where('type', 'week'))
                .get(),
            'section_settings.week_number'
        );

        return findIndex(allSections, section => section.id === this.id) + 1;
    }

    static prospectiveTimeRanges(selectedSectionIds, times, editing = false) {
        const { applyFromDay, applyFromTime, applyToDay, applyToTime, fromDay, fromTime, toDay, toTime, timePeriodId } =
            times;

        const original = TimePeriod.find(timePeriodId);

        let originalFrom;
        let originalTo;

        if (original) {
            originalFrom = original.timestamp('from');
            originalTo = original.timestamp('to');
        }

        return Section.query()
            .with('section_settings')
            .whereIdIn(selectedSectionIds)
            .get()
            .map(section => {
                const from = section.section_settings.timestamp('week_start');
                const to = section.section_settings.timestamp('week_start');

                if (applyFromDay || !editing) {
                    from.add(fromDay - 1, 'days');
                } else if (original) {
                    from.add(originalFrom.isoWeekday(), 'days');
                }

                if (applyFromTime || !editing) {
                    from.hour(fromTime.getHours()).minute(fromTime.getMinutes());
                } else if (original) {
                    from.hour(originalFrom.hour).minute(originalFrom.minute);
                }

                if (applyToDay || !editing) {
                    to.add(toDay - 1, 'days');
                } else if (original) {
                    to.add(originalTo.isoWeekday(), 'days');
                }

                if (applyToTime || !editing) {
                    to.hour(toTime.getHours()).minute(toTime.getMinutes());
                } else if (original) {
                    to.hour(originalTo.hour).minute(originalTo.minute);
                }

                return [from, to, timePeriodId];
            });
    }
}
